// Phase 4.3: Dashboard View with Global Filters and Widget Grid
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation, Link, useSearchParams } from "react-router-dom";
import { RefreshCw, Edit, Calendar, LogIn, Plus } from "lucide-react";

import { FavoriteStarButton } from "@/features/favorites/FavoriteStarButton";
import { fetchDashboard } from "@/features/dashboards/api";
import { AddCardModal } from "@/features/dashboards/AddCardModal";
import {
  createWidget,
  type RegistrableWidgetType,
} from "@/features/dashboards/widget-registry";
import { useAuth } from "@/state/AuthContext";
import { queryWidgets } from "@/features/widgets/api";
import FiltersBar from "@/features/dashboards/FiltersBar";
import { parseFiltersFromUrl, filtersToUrl, type DashboardFilters } from "@/features/dashboards/filters";
import { WidgetRenderer } from "@/features/dashboards/widgets/WidgetRenderer";
import { track } from "@/lib/telemetry";
import type { DashboardEntity } from "@/features/dashboards/types";
import { WorkspaceRequiredError } from "@/features/dashboards/schemas";

export default function DashboardView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  // DASH-SHARE-UI-OFF-1 — public ?share= links are disabled (API 410).
  const legacyShareToken = searchParams.get('share');

  const [dashboard, setDashboard] = useState<DashboardEntity | null>(null);
  const [widgetsData, setWidgetsData] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [workspaceError, setWorkspaceError] = useState(false);
  const [addCardOpen, setAddCardOpen] = useState(false);

  // Global filters state
  const urlFilters = useMemo(() => parseFiltersFromUrl(location.search), [location.search]);
  const [globalFilters, setGlobalFilters] = useState<DashboardFilters>(() => {
    // Initialize from URL or defaults
    const parsed = parseFiltersFromUrl(location.search);
    return parsed;
  });

  // Convert filters to date range for analytics widgets
  const dateRange = useMemo(() => {
    const today = new Date();
    const startDate = new Date(today);

    switch (globalFilters.timeRange) {
      case '7d':
        startDate.setDate(today.getDate() - 7);
        break;
      case '14d':
        startDate.setDate(today.getDate() - 14);
        break;
      case '30d':
        startDate.setDate(today.getDate() - 30);
        break;
      case '90d':
        startDate.setDate(today.getDate() - 90);
        break;
      case 'custom':
        if (globalFilters.from) {
          startDate.setTime(new Date(globalFilters.from).getTime());
        } else {
          startDate.setDate(today.getDate() - 30);
        }
        break;
      default:
        startDate.setDate(today.getDate() - 30);
    }

    const endDate = globalFilters.to ? new Date(globalFilters.to) : today;

    return {
      startDate: startDate.toISOString().split('T')[0], // YYYY-MM-DD
      endDate: endDate.toISOString().split('T')[0],
    };
  }, [globalFilters]);

  // Load dashboard
  const loadDashboard = async () => {
    try {
      setLoading(true);
      setError(null);
      setWorkspaceError(false);

      const d = await fetchDashboard(id!);
      setDashboard(d);
    } catch (e: any) {
      if (e instanceof WorkspaceRequiredError) {
        setWorkspaceError(true);
        setError("Please select a workspace to view this dashboard.");
      } else {
        const requestId = e?.response?.headers?.['x-request-id'];
        setError(e?.response?.data?.message || `Failed to load dashboard${requestId ? ` (RequestId: ${requestId})` : ''}`);
        // Still render shell with fallback
        setDashboard({
          id: id!,
          name: "Dashboard",
          widgets: [],
          visibility: "WORKSPACE",
          workspaceId: "",
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        } as DashboardEntity);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Depend on signed-in presence, not user object identity (AuthContext may
  // return a fresh object each render).
  const isSignedIn = Boolean(user);
  useEffect(() => {
    if (legacyShareToken && !isSignedIn) return;
    void loadDashboard();
  }, [id, legacyShareToken, isSignedIn]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadDashboard();
  };

  // 2) Query widgets when dashboard/filters are ready
  useEffect(() => {
    console.log('[DashboardView] Widget query effect triggered:', { dashboard, urlFilters });
    if (!dashboard) return;
    const widgets = dashboard.widgets || [];
    console.log('[DashboardView] Widgets to query:', widgets);
    if (!widgets.length) {
      setWidgetsData({});
      return;
    }
    const controller = new AbortController();
    (async () => {
      try {
        console.log('[DashboardView] Starting widget query...');

        // Existing generic widget query
        const generic = await queryWidgets(
          widgets.map(w => ({ id: w.id, type: w.widgetKey, config: w.config })),
          urlFilters,
          controller.signal,
        );
        console.log('[DashboardView] Widget query results:', generic);
        // FE-only `kpi` dropped (Agent 2 / DASH-WIDGET-RESILIENCE-1) — no special KPI fetch.
        setWidgetsData(generic || {});
      } catch (e) {
        console.warn("Widget query failed", e);
        setWidgetsData({});
      }
    })();
    return () => controller.abort();
  }, [dashboard, urlFilters]);

  const onApplyFilters = (filters: DashboardFilters) => {
    setGlobalFilters(filters);
    const url = filtersToUrl(filters);
    navigate({ search: url }, { replace: true });
    track("ui.db.filters.apply", { id, filters });
  };

  // DASH-SHARE-UI-OFF-1 — legacy public share URLs require login; do not fetch anonymously.
  if (legacyShareToken && !user) {
    return (
      <div className="p-6" data-testid="dashboard-view">
        <div className="mx-auto max-w-md rounded-lg border border-slate-200 bg-white p-6 text-center shadow-sm">
          <p className="text-sm font-medium text-slate-900">Public links are turned off.</p>
          <p className="mt-2 text-sm text-slate-600">Sign in to open this dashboard.</p>
          <Link
            to="/login"
            className="mt-4 inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            data-testid="share-sign-in"
          >
            <LogIn className="mr-2 h-4 w-4" />
            Sign in
          </Link>
        </div>
      </div>
    );
  }

  // Workspace required error handler
  if (workspaceError) {
    return (
      <div className="p-6" data-testid="dashboard-view">
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <p className="text-yellow-800">Please select a workspace to view this dashboard.</p>
        </div>
      </div>
    );
  }

  const name = dashboard?.name ?? "Dashboard";
  const widgets = dashboard?.widgets ?? [];

  return (
    <div className="p-6 space-y-4" data-testid="dashboard-view">
      {/* Top Toolbar */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900" data-testid="dashboard-title">
          {name}
        </h1>
        <div className="flex items-center gap-2">
          {id ? (
            <FavoriteStarButton
              itemType="dashboard"
              itemId={id}
              itemName={name}
              size="md"
              testId="dashboard-header-favorite-star"
            />
          ) : null}
          {/* Date Range Selector */}
          <div className="flex items-center border border-gray-300 rounded-md">
            <Calendar className="w-4 h-4 text-gray-400 mx-2" />
            <select
              value={globalFilters.timeRange}
              onChange={(e) => {
                const newFilters = { ...globalFilters, timeRange: e.target.value as any };
                onApplyFilters(newFilters);
              }}
              className="px-3 py-2 text-sm border-0 focus:ring-0 focus:outline-none rounded-md"
            >
              <option value="7d">Last 7 days</option>
              <option value="14d">Last 14 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="custom">Custom</option>
            </select>
          </div>

          {/* Refresh Button */}
          <button
            data-testid="dashboard-refresh"
            onClick={handleRefresh}
            disabled={refreshing}
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            aria-label="Refresh dashboard"
            title="Refresh dashboard"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>

          <button
            onClick={() => setAddCardOpen(true)}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-md hover:bg-slate-50"
            data-testid="dashboard-add-card"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Card
          </button>

          <Link
            to={`/dashboards/${id}/edit`}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
            data-testid="dashboard-edit"
          >
            <Edit className="w-4 h-4 mr-2" />
            Edit
          </Link>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Filters */}
      <FiltersBar storageKey={`dash:${id}:filters`} onApply={onApplyFilters} />

      {/* Widgets Grid - Use stored layout */}
      <div data-testid="dashboard-widgets" className="grid grid-cols-12 gap-4">
        {loading && (
          <div className="col-span-12 animate-pulse space-y-2">
            <div className="h-6 w-48 bg-gray-200 rounded" />
            <div className="h-20 w-full bg-gray-200 rounded" />
          </div>
        )}

        {!loading && widgets.length === 0 && (
          <div className="col-span-12 text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <p className="text-gray-500">No widgets configured.</p>
            <Link
              to={`/dashboards/${id}/edit`}
              className="mt-4 inline-flex items-center px-4 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-700"
            >
              Add widgets
            </Link>
          </div>
        )}

        {!loading && widgets.map((w) => {
          // Use stored layout (x, y, w, h) for grid positioning
          const colSpan = w.layout?.w || 4;
          const rowSpan = w.layout?.h || 3;

          // Prepare filters for widget
          const widgetFilters = {
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            projectId: globalFilters.owner, // Map owner filter to projectId if needed
            resourceId: undefined, // TODO: Add resource filter if needed
          };

          return (
            <div
              key={w.id}
              className={`col-span-${colSpan} row-span-${rowSpan}`}
              style={{
                gridColumn: `span ${colSpan}`,
                gridRow: `span ${rowSpan}`,
              }}
            >
              <WidgetRenderer
                widget={w}
                data={widgetsData[w.id]}
                filters={widgetFilters}
              />
            </div>
          );
        })}
      </div>

      <AddCardModal
        open={addCardOpen}
        onClose={() => setAddCardOpen(false)}
        onSelect={async (widgetType: RegistrableWidgetType) => {
          if (!id) return;
          try {
            // Phase 4.7: persist via canonical POST /api/dashboards/:id/widgets.
            // The previous implementation PATCHed `widgets` through dashboard
            // update — the backend silently ignored that field, so the new
            // widget never persisted.
            const widget = createWidget(widgetType);
            const { createWidget: createWidgetApi } = await import(
              "@/features/dashboards/api"
            );
            await createWidgetApi(id, {
              widgetKey: widget.widgetKey,
              title: widget.title,
              config: widget.config,
              layout: widget.layout,
            });
            // Refresh dashboard to show new widget (with the real backend id)
            const fresh = await fetchDashboard(id);
            setDashboard(fresh);
            track("dashboard.card_added", { widgetType, dashboardId: id });
          } catch (err: any) {
            console.error("Failed to add card:", err);
          }
        }}
      />
    </div>
  );
}

/** CSV export using dashboard name if present */
function exportCsv(dashboardName: string, all: Record<string, any>) {
  const rows = Object.entries(all).flatMap(([wid, payload]) => {
    if (!payload?.rows) return [];
    return payload.rows.map((r: any) => ({ widgetId: wid, ...r }));
  });

  // trivial CSV
  const header = rows.length ? Object.keys(rows[0]) : ["widgetId"];
  const csv =
    header.join(",") +
    "\n" +
    rows.map(r => header.map(h => JSON.stringify(r[h] ?? "")).join(",")).join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${dashboardName || "dashboard"}.csv`; // ✅ filename uses dashboard name
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

